import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Typography, Button, Spin, message, Form, Input, Switch, Select, Tag, Tabs, Upload, Space,
} from 'antd';
import { ArrowLeft, Save, Upload as UploadIcon, Image as ImageIcon } from 'lucide-react';
import { blogApi, uploadsApi } from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const statusLabels: Record<string, string> = {
  DRAFT: 'Borrador',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Archivado',
};

const statusColors: Record<string, string> = {
  DRAFT: 'default',
  PUBLISHED: 'green',
  ARCHIVED: 'orange',
};

const CATEGORIES = ['Mercado Inmobiliario', 'Guías', 'Lifestyle', 'Inversión', 'Noticias'];

export default function BlogPostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    blogApi.get(id)
      .then((r) => {
        setPost(r.data);
        const data = r.data;
        form.setFieldsValue({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          content: data.content,
          category: data.category,
          tags: data.tags ? (Array.isArray(data.tags) ? data.tags.join(', ') : data.tags) : '',
          status: data.status,
          published: data.published,
          metaTitle: data.metaTitle,
          metaDescription: data.metaDescription,
        });
        setCoverPreview(data.coverImage || null);
      })
      .catch(() => message.error('Error al cargar el artículo'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload: any = {
        title: values.title,
        slug: values.slug,
        excerpt: values.excerpt || null,
        content: values.content || null,
        category: values.category || null,
        tags: values.tags ? values.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
        status: values.status,
        published: values.published || false,
        coverImage: coverPreview || null,
        metaTitle: values.metaTitle || null,
        metaDescription: values.metaDescription || null,
      };

      await blogApi.update(id!, payload);
      message.success('Artículo guardado');

      // Refresh post
      const r = await blogApi.get(id!);
      setPost(r.data);
    } catch (err: any) {
      if (err?.errorFields) return; // validation error
      message.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const res = await uploadsApi.uploadFiles('blog', Array.from(files));
      const urls: string[] = res.data.urls || res.data.files || [];
      if (urls.length > 0) {
        setCoverPreview(urls[0]);
        message.success('Imagen de portada subida');
      }
    } catch {
      message.error('Error al subir imagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  if (!post) return <div style={{ textAlign: 'center', padding: 80 }}><Text type="secondary">Artículo no encontrado</Text></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeft size={16} />} onClick={() => navigate('/blog')} />
          <div>
            <Title level={3} style={{ margin: 0 }}>{post.title}</Title>
            <Space size={8} style={{ marginTop: 4 }}>
              <Tag color={statusColors[post.status]}>{statusLabels[post.status] || post.status}</Tag>
              {post.category && <Tag>{post.category}</Tag>}
              {post.author?.name && <Text type="secondary">por {post.author.name}</Text>}
            </Space>
          </div>
        </Space>
      </div>

      {/* Edit form with tabs */}
      <Form form={form} layout="vertical">
        <Tabs defaultActiveKey="content" items={[
          {
            key: 'content',
            label: 'Contenido',
            children: (
              <Card>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
                  <div>
                    <Form.Item name="title" label="Título" rules={[{ required: true, message: 'El título es obligatorio' }]}>
                      <Input placeholder="Título del artículo" />
                    </Form.Item>
                    <Form.Item name="slug" label="Slug (URL)">
                      <Input placeholder="mi-articulo" addonBefore="/" />
                    </Form.Item>
                    <Form.Item name="excerpt" label="Extracto">
                      <TextArea rows={3} placeholder="Breve descripción del artículo que aparecerá en los listados..." />
                    </Form.Item>
                    <Form.Item name="content" label="Contenido">
                      <TextArea rows={18} placeholder="Escribe el contenido del artículo aquí..." style={{ fontFamily: 'monospace', fontSize: 13 }} />
                    </Form.Item>
                  </div>
                  <div>
                    {/* Cover Image */}
                    <div style={{ marginBottom: 24 }}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>Imagen de portada</Text>
                      {coverPreview ? (
                        <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                          <img src={coverPreview} alt="Portada" style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8 }} />
                          <Button
                            size="small" danger type="primary"
                            style={{ position: 'absolute', top: 8, right: 8 }}
                            onClick={() => setCoverPreview(null)}
                          >✕</Button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            height: 200, border: '2px dashed #d9d9d9', borderRadius: 8,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', background: '#fafafa',
                          }}
                        >
                          <ImageIcon size={32} color="#999" />
                          <Text type="secondary" style={{ marginTop: 8 }}>Subir imagen de portada</Text>
                        </div>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleCoverUpload} />
                      {coverPreview && (
                        <Button size="small" block onClick={() => fileInputRef.current?.click()} loading={uploading}>
                          Cambiar imagen
                        </Button>
                      )}
                    </div>

                    {/* Sidebar fields */}
                    <Form.Item name="status" label="Estado">
                      <Select options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))} />
                    </Form.Item>
                    <Form.Item name="published" label="Publicar en web" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                    <Form.Item name="category" label="Categoría">
                      <Select allowClear placeholder="Selecciona categoría"
                        options={CATEGORIES.map(c => ({ value: c, label: c }))}
                      />
                    </Form.Item>
                    <Form.Item name="tags" label="Tags" extra="Separadas por comas">
                      <Input placeholder="inversión, marbella, luxury" />
                    </Form.Item>
                  </div>
                </div>
              </Card>
            ),
          },
          {
            key: 'seo',
            label: 'SEO',
            children: (
              <Card>
                <Form.Item name="metaTitle" label="Meta Title" extra="Si se deja vacío, se usará el título del artículo">
                  <Input placeholder="Título para buscadores" maxLength={70} showCount />
                </Form.Item>
                <Form.Item name="metaDescription" label="Meta Description" extra="Descripción para buscadores (max 160 caracteres)">
                  <TextArea rows={3} placeholder="Descripción SEO del artículo..." maxLength={160} showCount />
                </Form.Item>

                {/* SEO Preview */}
                <div style={{ marginTop: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Vista previa en Google</Text>
                  <div style={{ fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ color: '#1a0dab', fontSize: 18, marginBottom: 2 }}>
                      {form.getFieldValue('metaTitle') || form.getFieldValue('title') || 'Título del artículo'}
                    </div>
                    <div style={{ color: '#006621', fontSize: 13, marginBottom: 2 }}>
                      swomey.com/blog/{form.getFieldValue('slug') || 'slug-del-articulo'}
                    </div>
                    <div style={{ color: '#545454', fontSize: 13 }}>
                      {form.getFieldValue('metaDescription') || form.getFieldValue('excerpt') || 'Descripción del artículo...'}
                    </div>
                  </div>
                </div>
              </Card>
            ),
          },
        ]} />
      </Form>

      {/* Sticky save bar */}
      <div style={{
        position: 'sticky', bottom: 0, background: '#fff', padding: '12px 0',
        borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end',
        gap: 12, zIndex: 10, marginTop: 16,
      }}>
        <Button onClick={() => navigate('/blog')}>Cancelar</Button>
        <Button type="primary" icon={<Save size={14} />} onClick={handleSave} loading={saving}>
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}
